import { useCallback, useEffect, useState } from 'react';
import { Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NeuSelect } from '@/components/ui/neu-select';
import { useTipoEquipo, useCatalogoEco } from '@/api/catalogos';

const PILL_THRESHOLD = 6;

// ---------------------------------------------------------------------------
// Public hook — eliminates duplicated state + reset logic across pages
// ---------------------------------------------------------------------------

export interface EquipmentFilterState {
  tipoEquipoId: number | undefined;
  catalogoecoId: number | undefined;
  setTipoEquipoId: (id: number | undefined) => void;
  setCatalogoEcoId: (id: number | undefined) => void;
}

/** Manages equipment filter state and resets when `resetKey` changes. */
export function useEquipmentFilter(resetKey: number | string | null | undefined): EquipmentFilterState {
  const [tipoEquipoId, setTipoEquipoId] = useState<number | undefined>(undefined);
  const [catalogoecoId, setCatalogoEcoId] = useState<number | undefined>(undefined);

  useEffect(() => {
    setTipoEquipoId(undefined);
    setCatalogoEcoId(undefined);
  }, [resetKey]);

  return { tipoEquipoId, catalogoecoId, setTipoEquipoId, setCatalogoEcoId };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EquipoFilterProps {
  ambitoId?: number;
  tipoEquipoId?: number;
  catalogoecoId?: number;
  onTipoEquipoChange: (id: number | undefined) => void;
  onCatalogoEcoChange: (id: number | undefined) => void;
}

// ---------------------------------------------------------------------------
// Adaptive renderer: pills (<=6 items) or dropdown (>6 items)
// ---------------------------------------------------------------------------

interface AdaptiveFilterProps<T extends { id: number }> {
  label: string;
  items: T[];
  selected: number | undefined;
  onChange: (id: number | undefined) => void;
  displayKey: keyof T & string;
}

function AdaptiveFilter<T extends { id: number }>({
  label,
  items,
  selected,
  onChange,
  displayKey,
}: AdaptiveFilterProps<T>) {
  if (items.length === 0) return null;

  if (items.length <= PILL_THRESHOLD) {
    return (
      <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1">
        <button
          type="button"
          onClick={() => { onChange(undefined); }}
          className={cn(
            'shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors min-h-[28px]',
            selected === undefined
              ? 'bg-sime-accent text-white'
              : 'bg-sime-surface-raised text-sime-text-secondary hover:text-sime-text-primary',
          )}
        >
          Todos
        </button>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => { onChange(item.id === selected ? undefined : item.id); }}
            className={cn(
              'shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors min-h-[28px] max-w-[200px] truncate',
              selected === item.id
                ? 'bg-sime-accent text-white'
                : 'bg-sime-surface-raised text-sime-text-secondary hover:text-sime-text-primary',
            )}
          >
            {String(item[displayKey])}
          </button>
        ))}
      </div>
    );
  }

  return (
    <NeuSelect
      options={[
        { value: '', label: `Todos — ${label}` },
        ...items.map((item) => ({ value: String(item.id), label: String(item[displayKey]) })),
      ]}
      value={String(selected ?? '')}
      onChange={(v) => { onChange(v ? Number(v) : undefined); }}
      aria-label={label}
    />
  );
}

// ---------------------------------------------------------------------------
// FilterSection — reduces duplication in the two label+filter blocks
// ---------------------------------------------------------------------------

function FilterSection<T extends { id: number }>({
  title,
  ...filterProps
}: { title: string } & AdaptiveFilterProps<T>) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Filter className="h-3 w-3 text-sime-text-tertiary" />
        <span className="text-[11px] font-medium text-sime-text-tertiary uppercase tracking-wider">
          {title}
        </span>
      </div>
      <AdaptiveFilter {...filterProps} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EquipoFilter — 2-level filter (tipo_equipo -> catalogoeco)
// ---------------------------------------------------------------------------

export function EquipoFilter({
  ambitoId,
  tipoEquipoId,
  catalogoecoId,
  onTipoEquipoChange,
  onCatalogoEcoChange,
}: EquipoFilterProps) {
  const { data: tipoEquipos = [], isLoading: loadingTipo, isError: errorTipo } = useTipoEquipo(ambitoId);
  const { data: ecoItems = [], isLoading: loadingEco, isError: errorEco } = useCatalogoEco(ambitoId, tipoEquipoId);

  const handleTipoEquipoChange = useCallback(
    (id: number | undefined) => {
      onTipoEquipoChange(id);
      onCatalogoEcoChange(undefined);
    },
    [onTipoEquipoChange, onCatalogoEcoChange],
  );

  if (!ambitoId) return null;

  if (errorTipo || errorEco) {
    return (
      <div className="flex items-center gap-2 text-xs text-sime-danger py-1">
        <Filter className="h-3 w-3" />
        <span>Error cargando filtros de equipo</span>
      </div>
    );
  }

  if (loadingTipo || loadingEco) {
    return (
      <div className="flex gap-2 animate-pulse py-1">
        <div className="h-7 w-32 bg-sime-surface-raised rounded-full" />
        <div className="h-7 w-24 bg-sime-surface-raised rounded-full" />
      </div>
    );
  }

  if (tipoEquipos.length === 0 && ecoItems.length === 0) return null;

  return (
    <div className="space-y-2">
      {tipoEquipos.length > 0 && (
        <FilterSection
          title="Tipo Equipo"
          label="Tipo Equipo"
          items={tipoEquipos}
          selected={tipoEquipoId}
          onChange={handleTipoEquipoChange}
          displayKey="detalle"
        />
      )}

      {/* Visible sin necesidad de elegir tipo de equipo — el usuario filtra por
          ECO directo; elegir un tipo sigue acotando la lista (cascade). */}
      {ecoItems.length > 0 && (
        <FilterSection
          title="Capacidad / ECO"
          label="Capacidad"
          items={ecoItems}
          selected={catalogoecoId}
          onChange={onCatalogoEcoChange}
          displayKey="eco"
        />
      )}
    </div>
  );
}
